import {
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import OptimusButton from '../../../../common/library/Button/Button';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import { usePartnerProfileConfig } from '../Enrollment/usePartnerProfileConfig';
import { getCanonicalPartnerName } from '../../../../common/constants';

type PartnerContactSectionV2Props = {
  partnerName: string;
  partnerCode?: string;
};

export const PartnerContactSection = (props: PartnerContactSectionV2Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { openExternalLink } = useNavigationUtils();
  const { trackPartnerDetail } = useCustomEventTracking();
  const { partnerContacts } = usePartnerProfileConfig();

  // Derive the analytics event name from the stable code (via canonical name) so
  // it stays constant if the partner is renamed; fall back to the live name.
  const normalizedPartnerName = getCanonicalPartnerName(props.partnerCode, props.partnerName)
    ?.trim().replace(/\s+/g, '');

  return (
    <>
      {partnerContacts?.map((contact, index) => (
        <Box
          data-testid={`partners-individual-contact-${index}`}
          key={index}
          sx={{
            display: 'flex',
            padding: theme.spacing(2),
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: theme.spacing(2),
            alignSelf: 'stretch',
            borderRadius: theme.spacing(2.5),
            border: `1px solid ${theme.palette.grey[200]}`,
          }}>
          <Box>
            <Typography variant='body7'>
              {t('partnerDetails.contactHeading')}
            </Typography><br />
            <Typography variant='body8' color={theme.palette.grey[700]}>
              {contact.name && <span>{contact.name}<br /></span>}
              {contact.jobTitle && <span>{contact.jobTitle}<br /></span>}
              {contact.addressStreet && <span>{contact.addressStreet}<br /></span>}
              {/* {contact.addressCity}, {contact.addressState} {contact.addressZip}<br /> */}
              {(contact.addressCity || contact.addressState || contact.addressZip) && (
                <span>
                  {contact.addressCity && <>{contact.addressCity}</>},
                  {contact.addressState && <> {contact.addressState}</>}
                  {contact.addressZip && <> {contact.addressZip}</>}
                  <br />
                </span>
              )}
              {contact.email && <span>{contact.email}<br /></span>}
              {contact.phone && <span>{contact.phone}<br /></span>}
              {contact.website && <span>{contact.website}</span>}
            </Typography>
          </Box>
          {contact.website?.trim() && (
            <OptimusButton
              data-testid={'partners-individual-website-button'}
              size={'m'}
              customVariant='secondaryGray'
              leadingIcon={OpenInNewIcon}
              sx={{
                fontWeight: 600,
                fontSize: '14px',
              }}
              type="button"
              onClick={() => {
                trackPartnerDetail(
                  'Visit',
                  `${normalizedPartnerName}Website`,
                );
                openExternalLink(contact.website || '');
              }}
              buttonText={`${t('partnerDetails.visitWebsite')}`}
            />
          )}
        </Box>
      ))}
    </>
  );
};
